import { css, keyframes } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import type { TriggerBtnVariant } from "./DropdownMenu.ts";

export const containerStyle = css({
  height: "fit-content",
  width: "fit-content",
  minWidth: 60,
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
});

const slideUpAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateY(2px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const slideRightAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateX(-2px)" },
  "100%": { opacity: 1, transform: "translateX(0)" },
});

const slideDownAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateY(-2px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const slideLeftAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateX(2px)" },
  "100%": { opacity: 1, transform: "translateX(0)" },
});

const contentStyles =
  (customWidth: number | undefined) => (theme: ExactTheme) =>
    css({
      minWidth: customWidth ? customWidth : 128,
      zIndex: theme.zIndices.dropdowns,
      backgroundColor: theme.colorsV2.white,
      borderRadius: theme.borderRadiusV2.primary,
      boxShadow: theme.shadowV2.default,
      animationDuration: theme.animationDurationV2.dropdownArrowRotation,
      animationTimingFunction:
        theme.animationTimingFunctionV2.dropdownArrowRotation,
      willChange: "transform, opacity",
      '&[data-state="open"]': {
        '&[data-side="top"]': { animationName: slideDownAndFade },
        '&[data-side="right"]': { animationName: slideLeftAndFade },
        '&[data-side="bottom"]': { animationName: slideUpAndFade },
        '&[data-side="left"]': { animationName: slideRightAndFade },
      },
    });

const itemStyles =
  ({ selected }: { selected: boolean | undefined }) =>
  (theme: ExactTheme) =>
    css({
      all: "unset",
      fontSize: theme.fontSizesV2.body,
      fontWeight: selected
        ? theme.fontWeights.medium
        : theme.fontWeights.normal,
      lineHeight: 1,
      ":last-of-type": {
        borderBottomLeftRadius: 6,
        borderBottomRightRadius: 6,
      },
      ":first-of-type": {
        borderTopLeftRadius: 6,
        borderTopRightRadius: 7,
      },
      display: "flex",
      alignItems: "center",
      height: 35,
      padding: "0 10px",
      userSelect: "none",
      backgroundColor: selected ? theme.colorsV2.grey[50] : "white",
      color: selected ? theme.colorsV2.grey[900] : theme.colorsV2.grey[800],
      cursor: "pointer",

      "&[data-disabled]": {
        color: theme.colorsV2.grey[500],
        pointerEvents: "none",
      },

      "&[data-highlighted]": {
        backgroundColor: theme.colorsV2.grey[50],
      },
    });

const separatorStyles = (theme: ExactTheme) =>
  css({
    height: 1,
    backgroundColor: theme.colorsV2.grey[100],
  });

const btnStyle =
  ({
    triggerBtnVariant,
    disabled,
  }: {
    triggerBtnVariant: TriggerBtnVariant;
    disabled?: boolean;
  }) =>
  (theme: ExactTheme) => {
    const btnVariantMap = {
      standard: {
        height: 35,
        transition: "color 0.1s ease",
        "&:hover": {
          color: theme.colorsV2.grey[600],
        },
      },
      filled: {
        height: 35,
        transition: "background 0.1s ease",
        "&:hover": {
          backgroundColor: theme.colorsV2.grey[50],
        },
      },
    };

    return css({
      all: "unset",
      userSelect: "none",
      cursor: disabled ? "default" : "pointer",
      pointerEvents: disabled ? "none" : "auto",
      opacity: disabled ? 0.5 : 1,
      fontSize: theme.fontSizesV2.body,
      fontFamily: "inherit",
      borderRadius: theme.borderRadiusV2.primary,
      width: "inherit",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 5,
      paddingLeft: 10,
      paddingRight: 10,
      color: theme.colorsV2.grey[800],
      backgroundColor: "inherit",
      willChange: "color, background",
      ...btnVariantMap[triggerBtnVariant],

      "#dm-btn-arrow": {
        willChange: "transform",
        transition: "transform 0.3s ease",
      },

      "&:hover": {
        backgroundColor: theme.colorsV2.grey[50],
      },
      "&[data-state='open']": {
        backgroundColor: theme.colorsV2.grey[50],
      },
      "&[data-state='open'] #dm-btn-arrow": {
        transform: "rotate(180deg)",
      },
    });
  };

export { contentStyles, itemStyles, separatorStyles, btnStyle };
